import { Contract } from '@/modules/Contract/types'
import PermanentAddress from '@/modules/Employee/components/Confirmation/PermanentAddress'
import { EMPLOYEE_GENDER } from '@/modules/Employee/constants'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Typogaphy, GridHookForm, Button } from 'vnetwork-ui'
import { GridHookFormProps } from 'vnetwork-ui/dist/components/GridHookForm'
import { getCustomerById } from '@/modules/Customer/services'
import { useState } from 'react'
import { User } from 'lucide-react'
import dayjs from 'dayjs'

interface DeclaresInformationProps {
    rootName: string
}

const DeclaresInformation: React.FC<DeclaresInformationProps> = ({
    rootName,
}) => {
    const { t } = useTranslation()
    const formControl = useFormContext<Contract>()
    const [isPrefilling, setIsPrefilling] = useState(false)

    const forRegister = useWatch({
        control: formControl.control,
        name: `${rootName}.domain_extra_fields.for` as Path<Contract>,
    })

    const customer_id = useWatch({
        control: formControl.control,
        name: 'customer_id',
    })

    const handlePrefillFromCustomer = async () => {
        if (!customer_id) {
            return
        }

        try {
            setIsPrefilling(true)
            const response = await getCustomerById(customer_id)
            const customer = response?.result

            if (!customer) return

            // Get current form values to copy owner address to ui address
            const currentValues = formControl.getValues()
            const ownerFields =
                currentValues?.order?.products?.[0]?.service_domain_metadata
                    ?.domain_extra_fields

            console.log('[DeclaresInfo] Current form values:', currentValues)
            console.log('[DeclaresInfo] Owner fields:', ownerFields)

            // Prefill declares (UI) information from customer data
            formControl.setValue(
                `${rootName}.domain_extra_fields.uiName` as Path<Contract>,
                customer.customer_name || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.uiEmail` as Path<Contract>,
                customer.customer_email || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.uiPhone` as Path<Contract>,
                customer.customer_phone || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.uiPosition` as Path<Contract>,
                customer.customer_position || '',
                { shouldValidate: true }
            )

            // Copy address fields from owner (Step 1) to UI (Step 2)
            if (ownerFields) {
                console.log('[DeclaresInfo] Copying address fields...')

                // Copy country
                if (
                    ownerFields.ownerCountry &&
                    typeof ownerFields.ownerCountry === 'object'
                ) {
                    console.log(
                        '[DeclaresInfo] Copying ownerCountry:',
                        ownerFields.ownerCountry
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.uiCountry` as Path<Contract>,
                        ownerFields.ownerCountry,
                        { shouldValidate: true, shouldDirty: true }
                    )
                } else {
                    console.warn(
                        '[DeclaresInfo] ownerCountry not found or invalid:',
                        ownerFields.ownerCountry
                    )
                }

                // Copy province
                if (
                    ownerFields.ownerProvince &&
                    typeof ownerFields.ownerProvince === 'object'
                ) {
                    console.log(
                        '[DeclaresInfo] Copying ownerProvince:',
                        ownerFields.ownerProvince
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.uiProvince` as Path<Contract>,
                        ownerFields.ownerProvince,
                        { shouldValidate: true, shouldDirty: true }
                    )
                } else {
                    console.warn(
                        '[DeclaresInfo] ownerProvince not found or invalid'
                    )
                }

                // Copy district
                if (
                    ownerFields.ownerDistrict &&
                    typeof ownerFields.ownerDistrict === 'object'
                ) {
                    console.log(
                        '[DeclaresInfo] Copying ownerDistrict:',
                        ownerFields.ownerDistrict
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.uiDistrict` as Path<Contract>,
                        ownerFields.ownerDistrict,
                        { shouldValidate: true, shouldDirty: true }
                    )
                }

                // Copy ward
                if (
                    ownerFields.ownerWard &&
                    typeof ownerFields.ownerWard === 'object'
                ) {
                    console.log(
                        '[DeclaresInfo] Copying ownerWard:',
                        ownerFields.ownerWard
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.uiWard` as Path<Contract>,
                        ownerFields.ownerWard,
                        { shouldValidate: true, shouldDirty: true }
                    )
                }

                // Copy address
                if (ownerFields.ownerAddress) {
                    console.log(
                        '[DeclaresInfo] Copying ownerAddress:',
                        ownerFields.ownerAddress
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.uiAddress` as Path<Contract>,
                        ownerFields.ownerAddress,
                        { shouldValidate: true, shouldDirty: true }
                    )
                }
            } else {
                console.warn(
                    '[DeclaresInfo] Owner fields not found! Please fill Step 1 first.'
                )
            }

            // Prefill company name if exists
            if (customer.company) {
                formControl.setValue(
                    `${rootName}.domain_extra_fields.uiCompany` as Path<Contract>,
                    customer.company.company_name || '',
                    { shouldValidate: true }
                )

                // If company registration, prefill tax code
                if (forRegister === 'congty') {
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.uiTaxCode` as Path<Contract>,
                        customer.company.company_tax || '',
                        { shouldValidate: true }
                    )
                }
            }

            // Prefill birthdate if available (for personal registration)
            if (customer.customer_dob && forRegister === 'canhan') {
                formControl.setValue(
                    `${rootName}.domain_extra_fields.uiBirthdate` as Path<Contract>,
                    dayjs(customer.customer_dob),
                    { shouldValidate: true }
                )
            }
        } catch (error) {
            console.error('Failed to prefill declares info:', error)
        } finally {
            setIsPrefilling(false)
        }
    }

    const renderForm = (
        forRegister: string
    ): Pick<GridHookFormProps<Contract>, 'data'> => {
        return {
            data:
                forRegister === 'canhan'
                    ? [
                          {
                              formItemProps: {
                                  name: `${rootName}.domain_extra_fields.uiID_Number` as Path<Contract>,
                                  type: 'textfield',
                                  label: t('uiID_Number'),
                                  placeholder: t('uiID_Number'),
                              },
                              className: 'col-span-2',
                          },
                      ]
                    : [
                          {
                              formItemProps: {
                                  name: `${rootName}.domain_extra_fields.uiTaxCode` as Path<Contract>,
                                  type: 'textfield',
                                  label: t('uiTaxCode'),
                                  placeholder: t('uiTaxCode'),
                              },
                              className: 'col-span-2',
                          },
                      ],
        }
    }

    return (
        <div data-tour="vnnic-declares">
            <div className="flex items-center justify-between mb-2">
                <Typogaphy className="font-medium" size="medium">
                    {t('declares-information')}
                </Typogaphy>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePrefillFromCustomer}
                    disabled={!customer_id || isPrefilling}
                    loading={isPrefilling}
                    startIcon={<User size={16} />}
                >
                    {t('copy-from-owner-info')}
                </Button>
            </div>

            <GridHookForm
                formControl={formControl}
                actions={[]}
                data={[
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.for` as Path<Contract>,
                            type: 'radiogroup',
                            label: t('for'),
                            direction: 'row',
                            options: [
                                { value: 'canhan', label: 'Cá Nhân' },
                                { value: 'congty', label: 'Doanh Nghiệp' },
                            ],
                            defaultValue: 'canhan',
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiName` as Path<Contract>,
                            type: 'textfield',
                            label: t('uiName'),
                            placeholder: t('uiName'),
                        },
                        className: 'col-span-2',
                    },
                    ...(renderForm(forRegister)?.data || []),
                    {
                        renderEditComponent: () => {
                            return (
                                <PermanentAddress
                                    // @ts-ignore
                                    city_key={`${rootName}.domain_extra_fields.uiProvince`}
                                    // @ts-ignore
                                    district_key={`${rootName}.domain_extra_fields.uiDistrict`}
                                    // @ts-ignore
                                    ward_key={`${rootName}.domain_extra_fields.uiWard`}
                                    // @ts-ignore
                                    steet_key={`${rootName}.domain_extra_fields.uiAddress`}
                                    // @ts-ignore
                                    country_key={`${rootName}.domain_extra_fields.uiCountry`}
                                    padding={false}
                                    returnFullOption
                                    renderLabel={(value: any) => {
                                        return value?.name || value?.label
                                    }}
                                    valueMapper={(value: any) => {
                                        if (
                                            !value ||
                                            !value?.label ||
                                            !value?.value
                                        )
                                            throw new Error(
                                                'Something went wrong'
                                            )

                                        return {
                                            label: value?.label,
                                            value: value?.value,
                                        }
                                    }}
                                />
                            )
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiEmail` as Path<Contract>,
                            type: 'textfield',
                            label: t('uiEmail'),
                            placeholder: t('uiEmail'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiPhone` as Path<Contract>,
                            type: 'textfield',
                            label: t('uiPhone'),
                            placeholder: t('uiPhone'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiGender` as Path<Contract>,
                            type: 'select',
                            label: t('uiGender'),
                            placeholder: t('uiGender'),
                            options: EMPLOYEE_GENDER(t),
                        },
                        className: 'col-span-2',
                        hidden: forRegister === 'congty',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiFax` as Path<Contract>,
                            type: 'textfield',
                            label: t('uiFax'),
                            placeholder: t('uiFax'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiBirthdate` as Path<Contract>,
                            type: 'datepicker',
                            label: t('uiBirthdate'),
                            placeholder: t('uiBirthdate'),
                            defaultValue: null,
                        },
                        className: 'col-span-2',
                        hidden: forRegister === 'congty',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiCompany` as Path<Contract>,
                            type: 'textfield',
                            label: t('uiCompany'),
                            placeholder: t('uiCompany'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.uiPosition` as Path<Contract>,
                            type: 'textfield',
                            label: t('uiPosition'),
                            placeholder: t('uiPosition'),
                        },
                        className: 'col-span-2',
                    },
                ]}
                subForm
            />
        </div>
    )
}

export default DeclaresInformation
